import React, { useEffect, useRef } from 'react';
import PSPDFKit from '@nutrient-sdk/viewer';
import { configService } from '@web/services/singletons';
import { Constants } from '../../../../../../packages/core/config';

export const PdfHighlighterNew = ({}: {}) => {
    const containerRef = useRef(null);
    const queryParams = new URLSearchParams(window.location.search);
    const highlightedWordsParam = queryParams.get('highlightedWords');
    const documentUrl = queryParams.get('url');
    const pageNumber = parseInt(queryParams.get('pageNumber') || '1', 10);

    const highlightedWords = highlightedWordsParam ? decodeURIComponent(highlightedWordsParam).split(',') : [];

    useEffect(() => {
        const styleTag = document.createElement('style');
        styleTag.innerHTML = `
            div {
            overflow: hidden !important;
        }
        `;
        document.head.appendChild(styleTag);
        return () => {
            const styleTags = document.head.getElementsByTagName('style');
            for (let i = 0; i < styleTags.length; i++) {
                if (styleTags[i].innerHTML.includes('overflow: hidden !important')) {
                    document.head.removeChild(styleTags[i]);
                    break;
                }
            }
        };
    }, []);

    useEffect(() => {
        const container = containerRef.current;

        if (!container || !documentUrl || !highlightedWords) {
            return;
        }

        (async function () {
            await PSPDFKit.load({
                container,
                disableWebAssemblyStreaming: true,
                document: documentUrl || '',
                licenseKey: configService.get(Constants.Env.NutrientApiKey),
                baseUrl: `${window.location.protocol}//${window.location.host}/`,
                theme: PSPDFKit.Theme.DARK,
                initialViewState: new PSPDFKit.ViewState({
                    currentPageIndex: pageNumber - 1,
                    sidebarMode: PSPDFKit.SidebarMode.THUMBNAILS,
                    layoutMode: PSPDFKit.LayoutMode.SINGLE,
                }),
                editableAnnotationTypes: [],
                toolbarItems: [
                    ...PSPDFKit.defaultToolbarItems.filter((item) =>
                        ['zoom-in', 'zoom-out', 'zoom-mode'].includes(item.type)
                    ),
                    { type: 'spacer' },
                    ...PSPDFKit.defaultToolbarItems.filter((item) => ['pager'].includes(item.type)),
                    { type: 'spacer' },
                    ...PSPDFKit.defaultToolbarItems.filter((item) =>
                        ['export-pdf', 'search', 'print'].includes(item.type)
                    ),
                ],
            }).then(async (instance) => {
                const annotations = (
                    await Promise.all(
                        Array.from({ length: highlightedWords.length }).map((element, index) =>
                            instance.search(highlightedWords[index])
                        )
                    )
                ).flatMap((searchResults) =>
                    searchResults.reduce((acc: any, searchResult) => {
                        const { rectsOnPage, pageIndex } = searchResult;

                        const bbox = new PSPDFKit.Geometry.Rect(rectsOnPage.get(0));

                        return acc.concat(
                            new PSPDFKit.Annotations.HighlightAnnotation({
                                id: PSPDFKit.generateInstantId(),
                                pageIndex: pageIndex,
                                boundingBox: bbox,
                                rects: rectsOnPage,
                                color: PSPDFKit.Color.LIGHT_BLUE,
                            })
                        );
                    }, [])
                );

                await instance.create(annotations);
            });
        })();

        return () => {
            if (PSPDFKit) {
                PSPDFKit.unload(container);
            }
        };
    }, [documentUrl, highlightedWords, pageNumber]);

    return <div ref={containerRef} style={{ width: '100%', height: '100%', overflow: 'hidden !important' }} />;
};
